import React from 'react';

import UserOutlined from '@ant-design/icons/lib/icons/UserOutlined';
import { Typography } from 'antd';
import dayjs from 'dayjs';
import styled from 'styled-components';

import Avatar from 'common/Avatar';
import { CommentType } from 'types/comments';

import { getCommentActorLabel } from './helpers';
import { ACTOR_TYPE } from '../../../../constants';

const { Paragraph } = Typography;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;
`;

const CommentItem = styled.div`
  display: flex;
  gap: 10px;
`;

const StyledAvatar = styled(Avatar)`
  display: flex;
  align-items: flex-start;
`;

const CommentContent = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 4px;
`;

const CommentMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-family: var(--font-bold);
  color: var(--text-primary);
`;

const CommentDate = styled.span`
  font-size: 12px;
  color: var(--text-secondary);
  font-family: var(--font-regular);
`;

const CommentText = styled(Paragraph)`
  font-size: 14px;
  line-height: 20px;
  white-space: break-spaces;
  margin-bottom: 0;
`;

const CommentTextToggle = styled.span`
  color: var(--dark-blue);
  cursor: pointer;
`;

const CommentActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
`;

const CommentAction = styled.span`
  cursor: pointer;
  color: var(--dark-blue);
`;

type ListCommentProps = {
  comments: CommentType[];
  dataTestIdPrefix: string;
  onEdit: (comment: CommentType) => void;
  onDelete: (comment: CommentType) => void;
};

const ListComment = ({ comments, dataTestIdPrefix, onEdit, onDelete }: ListCommentProps) => (
  <List>
    {comments.map((item, index) => {
      const actor = item.created_by;
      const isActorObject = typeof actor === 'object' && actor !== null;
      const isStaff = isActorObject && (actor.is_staff || actor.is_superuser);
      const avatarName = typeof actor === 'string' ? actor : actor?.name;
      const avatarUrl = isActorObject ? actor.avatar ?? actor.avatar_url ?? undefined : undefined;

      return (
        <CommentItem key={item?.id ?? index}>
          {isStaff ? (
            <StyledAvatar size={30} type={ACTOR_TYPE.USER} icon={<UserOutlined />} />
          ) : (
            <StyledAvatar size={30} type={ACTOR_TYPE.USER} name={avatarName} src={avatarUrl} />
          )}
          <CommentContent>
            <CommentMeta>
              {getCommentActorLabel(item)}
              {item?.created_at && (
                <CommentDate>
                  {dayjs(item.created_at).format('MMM DD YYYY')}
                  {item?.updated_at && !dayjs(item.updated_at).isSame(item.created_at, 'second') ? ' · Edited' : ''}
                </CommentDate>
              )}
            </CommentMeta>
            <CommentText
              ellipsis={{
                rows: 4,
                expandable: 'collapsible',
                symbol: (expanded: boolean) => (
                  <CommentTextToggle>{expanded ? 'Show Less' : 'Show More'}</CommentTextToggle>
                ),
              }}
            >
              {item?.comment ?? item?.text ?? ''}
            </CommentText>
            {item?.can_edit && (
              <CommentActions>
                <CommentAction onClick={() => onEdit(item)} data-test-id={`${dataTestIdPrefix}-edit-${item?.id}`}>
                  Edit
                </CommentAction>
                <span> · </span>
                <CommentAction onClick={() => onDelete(item)} data-test-id={`${dataTestIdPrefix}-delete-${item?.id}`}>
                  Delete
                </CommentAction>
              </CommentActions>
            )}
          </CommentContent>
        </CommentItem>
      );
    })}
  </List>
);

export default ListComment;
